import { cloneDeep } from 'lodash';
import { MapObject } from '../../types/general';
import { ensureReturnArray } from '../array/arrayUtil';
import { isPlainObject } from '../object/objectUtil';
import { objectEmpty } from '../utils';

const mergeElasticBoolClauses = <T extends MapObject | null | undefined>(primaryValue: T, secondaryValue: T): MapObject | MapObject[] => {
    const primaryArray = ensureReturnArray(primaryValue);
    const secondaryArray = ensureReturnArray(secondaryValue);

    if (!primaryArray.length && !secondaryArray.length) return [];
    if (!primaryArray.length) return secondaryArray;
    if (!secondaryArray.length) return primaryArray;

    const normalized = secondaryArray.length > 1 ? { must: secondaryArray } : secondaryArray[0];
    return [...primaryArray, normalized];
};

const mergeObjectsOrArrays = <T extends MapObject | MapObject[] | null | undefined>(primaryValue: T, secondaryValue: T): MapObject | MapObject[] => {
    if (isPlainObject(primaryValue) && isPlainObject(secondaryValue)) return mergeElasticsearchQueries(primaryValue, secondaryValue);
    if (Array.isArray(primaryValue) && Array.isArray(secondaryValue)) return [...primaryValue, ...secondaryValue];
    if (primaryValue !== undefined && secondaryValue !== undefined) return [primaryValue, secondaryValue];

    return secondaryValue ?? primaryValue;
};

export const mergeElasticsearchQueries = (
    primaryQuery: MapObject | null | undefined,
    secondaryQuery: MapObject | null | undefined
): MapObject => {
    if (objectEmpty(primaryQuery) && objectEmpty(secondaryQuery)) return {};
    if (objectEmpty(primaryQuery)) return cloneDeep(secondaryQuery ?? {});
    if (objectEmpty(secondaryQuery)) return cloneDeep(primaryQuery ?? {});

    const merged: MapObject = cloneDeep(primaryQuery);
    const boolOperators = new Set(['must', 'should', 'must_not']);

    for (const key of Object.keys(secondaryQuery)) {
        const primaryValue = merged[key];
        const secondaryValue = secondaryQuery[key];

        merged[key] = boolOperators.has(key)
            ? mergeElasticBoolClauses(primaryValue, secondaryValue)
            : mergeObjectsOrArrays(primaryValue, secondaryValue);
    }

    return merged;
};

const elasticsearchUtils = {
    mergeElasticsearchQueries,
};

export default elasticsearchUtils;
